// AuthContext — wraps the SPA with auth state from authService.
//
// PKCE-only: no embedded password login. Use initiateSocialLogin from
// authService for the actual login action; this context just exposes the
// resulting state.
//
// VITE_AUTH_MODE=none (dev/staging): skip Keycloak entirely. A synthetic
// "Dev User" is provided immediately; no token exchange or redirect occurs.
// API calls carry no Authorization header (the API's WHP_AUTH_MODE=none
// companion accepts unauthenticated requests after an IP-CIDR gate check).
//
// Capabilities (docs/follow-ups-plan.md §5): once a login is established, GET
// /api/me is read once per signed-in subject and `capabilities.builder` says
// whether this login may use the builder pages (RequireBuilder sends it to
// Follow-ups when not); `capabilities.accommodations` says whether it is on the
// RRA roster, which keeps the Accommodations console open (in the minimal
// shell) for a login without the builder. null while that read is in flight.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api } from '@/lib/api';
import {
  ensureFreshToken,
  getAccessToken,
  getCurrentUser,
  hasAnyRole as checkHasAnyRole,
  hasRole as checkHasRole,
  initializeAuth,
  isAuthenticated as hasValidSession,
  logout as authLogout,
} from '@/lib/authService';

// VITE_AUTH_MODE: 'keycloak' (default, prod) | 'none' (dev/staging).
// `?.` so the module also loads under the node test runner, where Vite's
// import.meta.env does not exist (the build still replaces it statically).
const AUTH_MODE = import.meta.env?.VITE_AUTH_MODE === 'none' ? 'none' : 'keycloak';

export interface User {
  sub: string;
  email?: string;
  emailVerified?: boolean;
  name?: string;
  preferredUsername?: string;
  roles: string[];
}

// Synthetic dev/staging user — matches the API's synthetic user so any
// display fields (name, email) are consistent across the stack.
const DEV_SYNTHETIC_USER: User = {
  sub: 'dev-user',
  email: 'dev@welcomehps.com',
  name: 'Dev User',
  roles: ['admin'],
};

interface AuthContextState {
  initialized: boolean;
  authenticated: boolean;
  user: User | null;
  /** From GET /api/me once signed in; null until that answers (and while signed out). */
  capabilities: Capabilities | null;
  loading: boolean;
  error: Error | null;
  logout: () => Promise<void>;
  getToken: () => Promise<string | null>;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  refreshAuthState: () => Promise<void>;
}

/** What this login may do, from GET /api/me. */
export interface Capabilities {
  /** The builder pages (every AppShell route). False sends the login to Follow-ups. */
  builder: boolean;
  /**
   * The Accommodations console (the RRA roster, whatever the login's roles).
   * With `builder: false` it still opens /accommodations, in the minimal shell.
   */
  accommodations: boolean;
  /**
   * RRA v2.1 (F2): may open a front-line call sheet and see the follow-ups
   * "Verifier call" rows. True for any verified staff login until an
   * administrator sets RRA_CALLERS; membership after that.
   */
  rraCaller: boolean;
}

/** Everything open: no-auth mode (the synthetic admin) and a failed /api/me read. */
const OPEN_CAPABILITIES: Capabilities = { builder: true, accommodations: true, rraCaller: true };

/** The starting value: no-auth mode is the synthetic admin, so the builder is open at once. */
export function initialCapabilities(authMode: 'none' | 'keycloak'): Capabilities | null {
  return authMode === 'none' ? { ...OPEN_CAPABILITIES } : null;
}

/**
 * GET /api/me's `capabilities`. Anything but an explicit `false` keeps a
 * capability open: an API from before a capability existed does not answer it,
 * and it gates nothing (the API refuses on its own routes either way).
 */
export function capabilitiesFromMe(body: unknown): Capabilities {
  const caps = (
    body as { capabilities?: { builder?: unknown; accommodations?: unknown; rraCaller?: unknown } } | null | undefined
  )?.capabilities;
  return {
    builder: caps?.builder !== false,
    accommodations: caps?.accommodations !== false,
    rraCaller: caps?.rraCaller !== false,
  };
}

/**
 * Reads /api/me once per signed-in subject: a token refresh (every 60 s) or a
 * re-render with the same subject reuses the answer; a different subject reads
 * again. A failed read keeps the builder open: this is a navigation hint, the
 * API gate is the one that refuses, and failing closed would bounce an admin to
 * Follow-ups on a network blip (or a login /api/me refuses, such as an admin
 * signed in with a non-@welcomehps.com address).
 */
export function capabilitiesLoader(readMe: () => Promise<unknown>): (sub: string) => Promise<Capabilities> {
  let last: { sub: string; answer: Promise<Capabilities> } | null = null;
  return (sub) => {
    if (last?.sub !== sub) {
      last = {
        sub,
        answer: readMe().then(capabilitiesFromMe, (err: unknown) => {
          console.warn('GET /api/me failed; leaving the builder open (the API still gates it)', err);
          return { ...OPEN_CAPABILITIES };
        }),
      };
    }
    return last.answer;
  };
}

/** The one read behind the capabilities. */
export const fetchMe = (): Promise<unknown> => api.get<unknown>('/api/me');

const AuthContext = createContext<AuthContextState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  // In no-auth mode, pre-seed state with the synthetic user so the app
  // renders immediately as authenticated — no useEffect needed.
  const [initialized, setInitialized] = useState(AUTH_MODE === 'none');
  const [authenticated, setAuthenticated] = useState(AUTH_MODE === 'none');
  const [user, setUser] = useState<User | null>(AUTH_MODE === 'none' ? DEV_SYNTHETIC_USER : null);
  const [loading, setLoading] = useState(AUTH_MODE !== 'none');
  const [error, setError] = useState<Error | null>(null);
  const [capabilities, setCapabilities] = useState<Capabilities | null>(() =>
    initialCapabilities(AUTH_MODE),
  );
  const loadCapabilities = useMemo(() => capabilitiesLoader(fetchMe), []);
  // Latest `authenticated` value for the keep-alive loop to read without a
  // stale closure (and without re-creating the timer on every auth change).
  const authenticatedRef = useRef(authenticated);
  useEffect(() => {
    authenticatedRef.current = authenticated;
  }, [authenticated]);

  useEffect(() => {
    // No-auth mode: synthetic user is already set; skip Keycloak entirely.
    if (AUTH_MODE === 'none') return;

    (async () => {
      try {
        const stored = await initializeAuth();
        if (stored) {
          setUser(stored);
          setAuthenticated(true);
        }
        setInitialized(true);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Auth initialization failed'));
        setInitialized(true);
      } finally {
        setLoading(false);
      }
    })();

    // Keep the session alive: refresh shortly before the access token expires,
    // AND whenever the user returns to the tab or the network comes back. The
    // focus/visibility/online handlers cover laptop sleep and backgrounded tabs
    // (where setInterval is throttled or paused), which was the main reason
    // people were getting logged out. ensureFreshToken() also falls back to a
    // silent prompt=none re-auth, so a session whose refresh token expired (or
    // was cleared by an API 401) is transparently recovered from the still-alive
    // Google/Apple SSO session. We only flip to logged-out when both the refresh
    // AND the silent re-auth fail (tokens truly gone). Gated on the UI auth
    // state (via a ref, so no stale closure) so we don't fire silent iframes on
    // the login screen.
    const keepAlive = async () => {
      if (!authenticatedRef.current) return;
      const ok = await ensureFreshToken();
      if (!ok && !hasValidSession()) {
        setAuthenticated(false);
        setUser(null);
      }
    };
    const refreshInterval = setInterval(keepAlive, 60000);
    const onActive = () => {
      if (document.visibilityState === 'visible') void keepAlive();
    };
    document.addEventListener('visibilitychange', onActive);
    window.addEventListener('focus', onActive);
    window.addEventListener('online', onActive);

    return () => {
      clearInterval(refreshInterval);
      document.removeEventListener('visibilitychange', onActive);
      window.removeEventListener('focus', onActive);
      window.removeEventListener('online', onActive);
    };
  }, []);

  // Capabilities follow the signed-in subject, not the user object: the 60 s
  // keep-alive and refreshAuthState hand back a new object for the same person
  // and must not trigger another read.
  const signedInSub = authenticated ? (user?.sub ?? null) : null;
  useEffect(() => {
    if (AUTH_MODE === 'none') return;
    setCapabilities(null);
    if (!signedInSub) return;
    let current = true;
    void loadCapabilities(signedInSub).then((c) => {
      if (current) setCapabilities(c);
    });
    return () => {
      current = false;
    };
  }, [signedInSub, loadCapabilities]);

  const logout = useCallback(async () => {
    // In no-auth mode, logout is a no-op (no Keycloak session to revoke).
    if (AUTH_MODE === 'none') return;
    setLoading(true);
    try {
      await authLogout();
    } finally {
      setUser(null);
      setAuthenticated(false);
      setLoading(false);
    }
  }, []);

  // In no-auth mode, getToken returns null — the API accepts no-auth requests.
  const getToken = useCallback(() => (AUTH_MODE === 'none' ? Promise.resolve(null) : getAccessToken()), []);
  const hasRole = useCallback((r: string) => {
    if (AUTH_MODE === 'none') return DEV_SYNTHETIC_USER.roles.includes(r);
    return checkHasRole(r);
  }, []);
  const hasAnyRole = useCallback((rs: string[]) => {
    if (AUTH_MODE === 'none') return rs.some((r) => DEV_SYNTHETIC_USER.roles.includes(r));
    return checkHasAnyRole(rs);
  }, []);
  const refreshAuthState = useCallback(async () => {
    if (AUTH_MODE === 'none') return; // synthetic user never changes
    const u = getCurrentUser();
    if (u) {
      setUser(u);
      setAuthenticated(true);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        initialized,
        authenticated,
        user,
        capabilities,
        loading,
        error,
        logout,
        getToken,
        hasRole,
        hasAnyRole,
        refreshAuthState,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
